import React from 'react';
import { AlertTriangle, TrendingUp, TrendingDown, BarChart3 } from 'lucide-react';
import { SimulationResults } from '../types/api';
import { cn } from '../lib/utils';

interface BiasAnalysisProps {
  results: SimulationResults | null;
}

const BiasAnalysis: React.FC<BiasAnalysisProps> = ({ results }) => {
  if (!results || !results.ground_truth_distribution) {
    return null;
  }

  const totalGroundTruth = Object.values(results.ground_truth_distribution).reduce((a, b) => a + b, 0);
  const groundTruthSiPercentage = ((results.ground_truth_distribution.si || 0) / totalGroundTruth) * 100;
  const groundTruthNoPercentage = ((results.ground_truth_distribution.no || 0) / totalGroundTruth) * 100;
  
  const validResponses = results.si_count + results.no_count;
  const aiSiPercentage = validResponses > 0 ? (results.si_count / validResponses) * 100 : 0;
  const aiNoPercentage = validResponses > 0 ? (results.no_count / validResponses) * 100 : 0;
  
  const siBias = aiSiPercentage - groundTruthSiPercentage;
  const noBias = aiNoPercentage - groundTruthNoPercentage;
  
  const getBiasSeverity = (bias: number) => {
    const absBias = Math.abs(bias);
    if (absBias < 5) return { level: 'low', label: 'Low' };
    if (absBias < 15) return { level: 'medium', label: 'Medium' };
    return { level: 'high', label: 'High' };
  };
  
  const siSeverity = getBiasSeverity(siBias);
  const noSeverity = getBiasSeverity(noBias);
  
  const overallBias = (Math.abs(siBias) + Math.abs(noBias)) / 2;
  const overallSeverity = getBiasSeverity(overallBias);

  return (
    <div className="rounded-lg border bg-card p-5">
      <div className="flex items-center space-x-2 mb-5">
        <AlertTriangle className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-base font-semibold">Bias Analysis</h2>
      </div>

      {/* Overall Bias Summary */}
      <div className={cn(
        "mb-5 p-3 rounded-md border-l-2",
        overallSeverity.level === 'low' && "bg-muted/30 border-foreground",
        overallSeverity.level === 'medium' && "bg-muted/50 border-foreground/60",
        overallSeverity.level === 'high' && "bg-muted border-foreground/30"
      )}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium">Overall Bias</h3>
            <p className="text-xs text-muted-foreground">
              {overallSeverity.label} ({overallBias.toFixed(1)}% avg deviation)
            </p>
          </div>
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-muted text-foreground">
            {overallSeverity.label}
          </span>
        </div>
      </div>

      {/* Detailed Bias Analysis */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        {/* Yes Bias */}
        <div className="border rounded-md p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-medium">Yes Bias</h4>
            {siBias > 0 ? (
              <TrendingUp className="h-3 w-3 text-muted-foreground" />
            ) : (
              <TrendingDown className="h-3 w-3 text-muted-foreground" />
            )}
          </div>
          
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Truth:</span>
              <span>{groundTruthSiPercentage.toFixed(1)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">AI:</span>
              <span>{aiSiPercentage.toFixed(1)}%</span>
            </div>
            <div className="flex justify-between border-t pt-1">
              <span className="text-muted-foreground">Diff:</span>
              <span className="font-medium">
                {siBias > 0 ? '+' : ''}{siBias.toFixed(1)}%
              </span>
            </div>
          </div>
          
          <span className="mt-2 inline-block px-1.5 py-0.5 rounded text-[10px] bg-muted">
            {siSeverity.label}
          </span>
        </div>

        {/* No Bias */}
        <div className="border rounded-md p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-medium">No Bias</h4>
            {noBias > 0 ? (
              <TrendingUp className="h-3 w-3 text-muted-foreground" />
            ) : (
              <TrendingDown className="h-3 w-3 text-muted-foreground" />
            )}
          </div>
          
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Truth:</span>
              <span>{groundTruthNoPercentage.toFixed(1)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">AI:</span>
              <span>{aiNoPercentage.toFixed(1)}%</span>
            </div>
            <div className="flex justify-between border-t pt-1">
              <span className="text-muted-foreground">Diff:</span>
              <span className="font-medium">
                {noBias > 0 ? '+' : ''}{noBias.toFixed(1)}%
              </span>
            </div>
          </div>
          
          <span className="mt-2 inline-block px-1.5 py-0.5 rounded text-[10px] bg-muted">
            {noSeverity.label}
          </span>
        </div>
      </div>

      {/* Visual Comparison */}
      <div className="border-t pt-4">
        <h3 className="text-xs font-medium text-muted-foreground mb-3 flex items-center">
          <BarChart3 className="h-3 w-3 mr-1" />
          Comparison
        </h3>
        
        <div className="space-y-3">
          {/* Yes comparison */}
          <div>
            <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
              <span>Yes</span>
              <span>{groundTruthSiPercentage.toFixed(1)}% vs {aiSiPercentage.toFixed(1)}%</span>
            </div>
            <div className="flex space-x-2">
              <div className="flex-1 bg-muted rounded-full h-2 relative overflow-hidden">
                <div
                  className="bg-foreground h-full rounded-full"
                  style={{ width: `${groundTruthSiPercentage}%` }}
                />
              </div>
              <div className="flex-1 bg-muted rounded-full h-2 relative overflow-hidden">
                <div
                  className="bg-foreground/60 h-full rounded-full"
                  style={{ width: `${aiSiPercentage}%` }}
                />
              </div>
            </div>
          </div>
          
          {/* No comparison */}
          <div>
            <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
              <span>No</span>
              <span>{groundTruthNoPercentage.toFixed(1)}% vs {aiNoPercentage.toFixed(1)}%</span>
            </div>
            <div className="flex space-x-2">
              <div className="flex-1 bg-muted rounded-full h-2 relative overflow-hidden">
                <div
                  className="bg-foreground h-full rounded-full"
                  style={{ width: `${groundTruthNoPercentage}%` }}
                />
              </div>
              <div className="flex-1 bg-muted rounded-full h-2 relative overflow-hidden">
                <div
                  className="bg-foreground/60 h-full rounded-full"
                  style={{ width: `${aiNoPercentage}%` }}
                />
              </div>
            </div>
          </div>
          
          <div className="flex justify-between text-[10px] text-muted-foreground pt-1">
            <span>Ground Truth</span>
            <span>AI Prediction</span>
          </div>
        </div>
      </div>

      {/* Interpretation */}
      <div className="mt-4 p-3 bg-muted/30 rounded-md">
        <h4 className="text-xs font-medium mb-1.5">Interpretation</h4>
        <div className="text-[10px] text-muted-foreground space-y-0.5">
          <p>Positive bias: AI overestimates | Negative: AI underestimates</p>
          <p>Low (&lt;5%): Accurate | Medium (5-15%): Acceptable | High (&gt;15%): Significant</p>
        </div>
      </div>
    </div>
  );
};

export default BiasAnalysis;
